import { formatStatusLabel, getStatusChipColor } from '@/ui/primitives/statusChips';
import type { StatusChipStatus } from '@/ui/primitives/statusChips';
import type { ProjectDto } from '@flowqa/shared';

type DisplayStatus = StatusChipStatus | null | undefined;

export type ExecutionStatusCounts = {
  passed: number;
  failed: number;
  errored: number;
  skipped: number;
  untested: number;
};

type StatusOption<TStatus extends string> = {
  value: TStatus;
  label: string;
};

export const shouldGroupErroredWithFailed = (
  project: Pick<ProjectDto, 'testDataControls'> | null | undefined,
  selectedProjectId: string | null,
): boolean => {
  if (!selectedProjectId || !project) {
    return false;
  }
  return project.testDataControls.groupErroredWithFailed;
};

export const resolveProjectDisplayStatus = <TStatus extends DisplayStatus>(
  status: TStatus,
  groupErroredWithFailed: boolean,
): TStatus | Extract<TStatus, 'failed'> => {
  if (groupErroredWithFailed && status === 'errored') {
    return 'failed' as TStatus | Extract<TStatus, 'failed'>;
  }
  return status;
};

export const resolveProjectDisplayStatusLabel = (
  status: DisplayStatus,
  groupErroredWithFailed: boolean,
): string => {
  const displayStatus = resolveProjectDisplayStatus(status, groupErroredWithFailed);
  return formatStatusLabel(displayStatus);
};

export const resolveProjectDisplayStatusColor = (
  status: DisplayStatus,
  groupErroredWithFailed: boolean,
): ReturnType<typeof getStatusChipColor> => {
  const displayStatus = resolveProjectDisplayStatus(status, groupErroredWithFailed);
  return getStatusChipColor(displayStatus);
};

export const collapseExecutionStatusCounts = (
  counts: Partial<ExecutionStatusCounts>,
  groupErroredWithFailed: boolean,
): ExecutionStatusCounts => {
  const resolvedCounts: ExecutionStatusCounts = {
    passed: counts.passed ?? 0,
    failed: counts.failed ?? 0,
    errored: counts.errored ?? 0,
    skipped: counts.skipped ?? 0,
    untested: counts.untested ?? 0,
  };
  if (!groupErroredWithFailed) {
    return resolvedCounts;
  }
  return {
    ...resolvedCounts,
    failed: resolvedCounts.failed + resolvedCounts.errored,
    errored: 0,
  };
};

export const collapseStatusOptions = <TStatus extends string>(
  options: Array<StatusOption<TStatus>>,
  groupErroredWithFailed: boolean,
): Array<StatusOption<TStatus>> => {
  if (!groupErroredWithFailed) {
    return options;
  }
  return options
    .filter((option) => option.value !== ('errored' as TStatus))
    .map((option) =>
      option.value === ('failed' as TStatus)
        ? { ...option, label: formatStatusLabel('failed') }
        : option,
    );
};

export const expandStatusFilterValues = <TStatus extends string>(
  statuses: TStatus[],
  groupErroredWithFailed: boolean,
): TStatus[] => {
  if (!groupErroredWithFailed) {
    return statuses;
  }
  const expanded = new Set<TStatus>();
  statuses.forEach((status) => {
    if (status === ('failed' as TStatus) || status === ('errored' as TStatus)) {
      expanded.add('failed' as TStatus);
      expanded.add('errored' as TStatus);
      return;
    }
    expanded.add(status);
  });
  return [...expanded];
};
